import React, { useEffect, useMemo, useState } from 'react';

import { Row, Col, Typography, Table, Breadcrumb, Card, Space } from 'antd';
import { Content } from 'antd/es/layout/layout';
import '../../App.css';

import { LayersControl, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import { useLocation, useNavigate } from 'react-router-dom';
import { FullscreenOutlined, FullscreenExitOutlined, HomeOutlined } from '@ant-design/icons';
import SeaMap, { vesselCpaIcon } from './SeaMap';
import { useAuth } from '../Context';

import englan from '../../english.json';
import germanlan from '../../german.json';
import arabiclan from '../../arabic.json';
import frenchlan from '../../french.json';
import hindilan from '../../hindi.json';
import malayalamlan from '../../malayalam.json';
import { datetimeCovertertolocal } from '../datetime-converter';
import PropTypes from 'prop-types';
import GoogleMap from '../Map/map';

const { Title } = Typography;

const linecolor = { color: 'green' };

// Same green, but dotted — used for the moving PORT trail during PLAY.
const portLinecolor = { color: 'green', dashArray: '2,10', lineCap: 'round' };

// Kept identical (same order) to GMap.jsx's `algorithmColors` / SeaMap's
// `ALGORITHM_COLORS` so a given detectionAlgorithmTypeId always resolves
// to the same color everywhere.
const ALGORITHM_COLORS = [
	'#FF0000',
	'#0000FF',
	'#00AA00',
	'#FF9900',
	'#9900FF',
	'#00AAAA',
	'#FF00AA',
	'#795548',
	'#607D8B',
	'#8BC34A'
];

// ============================================================
// DETERMINISTIC ALGORITHM ID -> COLOR
//
// Identical hash to the one in SeaMap.jsx. This is what guarantees a
// given detectionAlgorithmTypeId ALWAYS resolves to the exact same
// color on this page too — e.g. algorithm A is always color X here,
// exactly like it is on the Realtime Detection list/map page.
//
// NOTE: SeaMap.jsx's own `colorForAlgorithmId(trajectory.detectionAlgorithmTypeId)`
// is what actually decides the rendered color (it takes priority over
// `trajectory.color` below). This local copy just keeps `detectionColor`
// consistent as a fallback for the rare case a trajectory doesn't carry
// an algorithm ID at all — previously this used to always fall back to
// `ALGORITHM_COLORS[0]` (red) regardless of which algorithm produced the
// detection, which is what made colors look inconsistent/wrong.
// ============================================================
const colorForAlgorithmId = (id) => {
	if (id === undefined || id === null || id === '') return null;
	const str = String(id);
	let hash = 0;
	for (let i = 0; i < str.length; i += 1) {
		hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
	}
	return ALGORITHM_COLORS[hash % ALGORITHM_COLORS.length];
};

export default function RealtimeDetectionDetails() {

	const dashboardtype = process.env.REACT_APP_IDENTIFIER;
	const authtoken = localStorage.getItem(`${dashboardtype.toUpperCase()}_ACCESS_TOKEN`);
	const { state } = useLocation();
	const navigate = useNavigate();
	const [size, setSize] = useState('200px');
	const map_type = localStorage.getItem(`${dashboardtype.toUpperCase()}_MAP_TYPE`);

	const { handleGetMapdata, mapdata, optionalpipedata, handleOptionalPipelinedata } = useAuth();

	const langid = localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) && localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) ? parseInt(localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`)) : 2;
	const langdata = langid && langid ==1 ? arabiclan : langid ==2 ? englan : langid ==3 ? frenchlan : langid ==4 ? germanlan : langid ==5 ? hindilan : langid == 6 ? malayalamlan : englan;

	// ---- Custom PLAY animation state (restored from the earlier working
	// version — SeaMap's own built-in trajectory PLAY control is disabled
	// on this page via showTrajectoryPlayControl={false} below). ----
	const [changePlayrealtimedetectionLine, setchangePlayrealtimedetectionLine] = useState(false);
	const [playrealtimedetectionLine, setRealtimedetectionLine] = useState([]);
	// PORT trail revealed in parallel with the source trail during PLAY.
	const [playPortRealtimedetectionLine, setPlayPortRealtimedetectionLine] = useState([]);

	

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (!state) {
			navigate(-1);
		}
	}, [state]);

	useEffect(() => {
		if (!authtoken) {
			navigate('/');
		} else if (map_type && map_type === 'leaflet' && state) {
			handleGetMapdata(authtoken, state.data.regioninfoId, null, null);
			handleOptionalPipelinedata(authtoken, null, state.data.regioninfoId, null, null);
		}
		
	}, [authtoken]);

	const record = state && state.data ? state.data : null;
	const coordinateMarkers = state && state.coordinateMarkers ? state.coordinateMarkers : null;
	const KMP = state && state.KMP ? state.KMP : null;
	const CH = state && state.CH ? state.CH : null;

	const distanceRangeTracks = useMemo(() => {
		const rawData = record || {};

		if (!mapdata || mapdata.length === 0 || Object.keys(rawData).length === 0) return [];

		const toNum = (v) =>
			v === null || v === undefined || v === '' ? NaN : Number(v);

		const toRad = (deg) => (deg * Math.PI) / 180;

		const haversineMeters = (a, b) => {
			const R = 6371000;
			const dLat = toRad(b.lat - a.lat);
			const dLng = toRad(b.lng - a.lng);

			const h =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(a.lat)) *
                Math.cos(toRad(b.lat)) *
                Math.sin(dLng / 2) ** 2;

			return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
		};

		// Channel id helper — change the field names here if your mapdata differs.
		const getChannelId = (obj) => {
			const c = obj?.channelId ?? obj?.channelinfoId ?? null;
			return c === null || c === undefined || c === '' ? null : c;
		};

		// 1) Line points per region (and per region+channel), in drawn order,
		// from FIBRE coordinates only. No pipeline fallback.
		const rawByRegion = {};

		mapdata.forEach((point) => {
			const lat = toNum(point?.fibreLatitude);
			const lng = toNum(point?.fibreLongitude);

			if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

			const od = toNum(point?.ODMeter);
			const regionKey = point?.regioninfoId ?? 'all';
			const channelId = getChannelId(point);
			const entry = { lat, lng, od: Number.isFinite(od) ? od : null };

			if (!rawByRegion[regionKey]) rawByRegion[regionKey] = [];
			rawByRegion[regionKey].push(entry);

			if (channelId !== null) {
				const channelKey = `${regionKey}|${channelId}`;
				if (!rawByRegion[channelKey]) rawByRegion[channelKey] = [];
				rawByRegion[channelKey].push({ ...entry });
			}
		});

		// 2) Give every point a distance along the line.
		const buildLine = (pts, cum, useOd) => {
			const knownIdx = [];

			pts.forEach((p, i) => {
				if (p.od !== null) knownIdx.push(i);
			});

			const dist = pts.map((p, i) => {
				if (!useOd || knownIdx.length < 2) return cum[i];

				if (p.od !== null) return p.od;

				let prev = -1;
				let next = -1;

				for (let k = 0; k < knownIdx.length; k += 1) {
					if (knownIdx[k] < i) prev = knownIdx[k];

					if (knownIdx[k] > i) {
						next = knownIdx[k];
						break;
					}
				}

				if (prev !== -1 && next !== -1) {
					const span = cum[next] - cum[prev];
					const ratio = span > 0 ? (cum[i] - cum[prev]) / span : 0;

					return pts[prev].od + (pts[next].od - pts[prev].od) * ratio;
				}

				if (prev !== -1) {
					return pts[prev].od + (cum[i] - cum[prev]);
				}

				return pts[next].od - (cum[next] - cum[i]);
			});

			let line = pts.map((p, i) => ({
				lat: p.lat,
				lng: p.lng,
				od: dist[i],
			}));

			if (line[0].od > line[line.length - 1].od) {
				line = line.reverse();
			}

			return line;
		};

		const lineByRegion = {};

		Object.keys(rawByRegion).forEach((key) => {
			const pts = rawByRegion[key];

			if (pts.length < 2) return;

			const cum = [0];

			for (let i = 1; i < pts.length; i += 1) {
				cum.push(
					cum[i - 1] + haversineMeters(pts[i - 1], pts[i])
				);
			}

			// Use ODMeter if it increases along the line; otherwise, use geometric length.
			let line = buildLine(pts, cum, true);

			const usable =
            line[line.length - 1].od > line[0].od &&
            line.every((p, i) =>	i === 0 || p.od >= line[i - 1].od - 1e-6
            );

			if (!usable) {
				line = buildLine(pts, cum, false);
			}

			lineByRegion[key] = line;
		});

		// 3) Exact position on the line for a given distance.
		const pointAtDistance = (line, distance) => {
			if (distance <= line[0].od) {
				return {
					lat: line[0].lat,
					lng: line[0].lng,
					od: line[0].od,
					next: 1,
				};
			}

			const last = line[line.length - 1];

			if (distance >= last.od) {
				return {
					lat: last.lat,
					lng: last.lng,
					od: last.od,
					next: line.length,
				};
			}

			for (let i = 0; i < line.length - 1; i += 1) {
				const a = line[i];
				const b = line[i + 1];

				if (distance >= a.od && distance <= b.od) {
					const span = b.od - a.od;
					const ratio = span > 0 ? (distance - a.od) / span : 0;

					return {
						lat: a.lat + (b.lat - a.lat) * ratio,
						lng: a.lng + (b.lng - a.lng) * ratio,
						od: distance,
						next: i + 1,
					};
				}
			}

			return {
				lat: last.lat,
				lng: last.lng,
				od: last.od,
				next: line.length,
			};
		};

		// 4) Convert distanceRange into [[min, max], ...].
		// Normally, there is one pair: [start, end].
		const getFragments = (rawRange) => {
			let range = rawRange;

			if (typeof range === 'string') {
				try {
					range = JSON.parse(range);
				} catch (e) {
					range = [];
				}
			}

			if (!Array.isArray(range)) return [];

			const nums = range
				.flat()
				.map(toNum)
				.filter(Number.isFinite);

			const fragments = [];

			for (let i = 0; i + 1 < nums.length; i += 2) {
				fragments.push([
					Math.min(nums[i], nums[i + 1]),
					Math.max(nums[i], nums[i + 1]),
				]);
			}

			return fragments;
		};

		const tracks = [];

		// record is an object, not an array.
		// distanceRange is directly available on rawData.
		const fragments = getFragments(rawData?.distanceRange);

		if (fragments.length === 0) return [];

		const regionKey = rawData?.regioninfoId ?? 'all';
		const recordChannelId = getChannelId(rawData);

		// Prefer the detection's own channel line, then the region line.
		const line =
    (recordChannelId !== null && lineByRegion[`${regionKey}|${recordChannelId}`]) ||
    lineByRegion[regionKey] ||
    lineByRegion.all ||
    Object.values(lineByRegion)[0];

		if (!line || line.length < 2) return [];

		const color = colorForAlgorithmId(
			rawData?.detectionalgorithmtypeId
				? rawData.detectionalgorithmtypeId
				: ''
		);

		fragments.forEach(([fragMin, fragMax], fragIndex) => {
			if (fragMin === fragMax) return;

			const start = pointAtDistance(line, fragMin);
			const end = pointAtDistance(line, fragMax);

			if (start.od === end.od) return;

			// Start, every line vertex strictly between them, and end.
			const positions = [[start.lat, start.lng]];

			for (let k = start.next; k < end.next; k += 1) {
				if (
					line[k] &&
                line[k].od > start.od &&
                line[k].od < end.od
				) {
					positions.push([line[k].lat, line[k].lng]);
				}
			}

			positions.push([end.lat, end.lng]);

			if (positions.length < 2) return;

			tracks.push({
				id: `${rawData?.id}-${rawData?.channelId ?? 'na'}-${fragIndex}`,
				channelId: rawData?.channelId ?? null,
				color,
				algorithmName: rawData?.detectionAlgorithmTypeName
					? rawData.detectionAlgorithmTypeName
					: '',
				channelName: rawData?.channelName
					? rawData.channelName
					: '',
				eventType: rawData?.eventType
					? rawData.eventType
					: '',
				minDistance: fragMin,
				maxDistance: fragMax,
				positions,
				startPoint: {
					lat: start.lat,
					lng: start.lng,
					distance: fragMin,
				},
				endPoint: {
					lat: end.lat,
					lng: end.lng,
					distance: fragMax,
				},
			});
		});

		return tracks;
	}, [record, mapdata]);

	const newRecord = [];
	if (state && state.data) {
		newRecord.push(record);
	}

	const detectionResultsData = record
		? (state && state.trajectoryboolean ? record.correctedDetectionResults : record.detectionResults)
		: null;

	let cpaIndex = -1;
	if (detectionResultsData && detectionResultsData.cpaSourceDistance != null && Array.isArray(detectionResultsData.sourceDistances)) {
		let smallestDifference = Infinity;
		detectionResultsData.sourceDistances.forEach((distance, index) => {
			const difference = Math.abs(Number(distance) - Number(detectionResultsData.cpaSourceDistance));
			if (difference < smallestDifference) {
				smallestDifference = difference;
				cpaIndex = index;
			}
		});
	}

	const detectionTrackPoints = [];
	if (detectionResultsData && Array.isArray(detectionResultsData.sourceLatitude) && Array.isArray(detectionResultsData.sourceLongitude)) {
		detectionResultsData.sourceLatitude.forEach((lat, index) => {
			const lon = detectionResultsData.sourceLongitude[index];
			if (lat !== undefined && lat !== null && lon !== undefined && lon !== null) {
				detectionTrackPoints.push({
					lat: parseFloat(lat),
					lon: parseFloat(lon),
					sbr: detectionResultsData.sbr ? detectionResultsData.sbr[index] : null,
					sourceAngle: detectionResultsData.sourceAngle ? detectionResultsData.sourceAngle[index] : null,
					maxBeamEnergy: detectionResultsData.maxBeamEnergy ? detectionResultsData.maxBeamEnergy[index] : null,
					sourceDistance: detectionResultsData.sourceDistances ? detectionResultsData.sourceDistances : null,
					timeStart: detectionResultsData.timeStart ? detectionResultsData.timeStart[index] : null,
					timeEnd: detectionResultsData.timeEnd ? detectionResultsData.timeEnd[index] : null,
					isCPA: index === cpaIndex,
				});
			}
		});
	}

	const line = detectionTrackPoints.map((p) => [p.lat, p.lon]);

	// ---- PORT track points (portLatitude / portLongitude) ----
	// Index-aligned with the source arrays; `index` is the ORIGINAL index so
	// SeaMap can look up the matching distance / speed / time for the hover.
	// Empty array when the detection has no port coordinates -> nothing is
	// plotted for it.
	const portTrackPoints = [];
	if (
		detectionResultsData &&
		Array.isArray(detectionResultsData.portLatitude) &&
		Array.isArray(detectionResultsData.portLongitude)
	) {
		detectionResultsData.portLatitude.forEach((lat, index) => {
			const lon = detectionResultsData.portLongitude[index];
			if (
				lat !== undefined && lat !== null &&
				lon !== undefined && lon !== null &&
				Number.isFinite(Number(lat)) && Number.isFinite(Number(lon))
			) {
				portTrackPoints.push({
					lat: Number(lat),
					lng: Number(lon),
					sbr: detectionResultsData.sbr ? detectionResultsData.sbr[index] : undefined,
					sourceAngle: detectionResultsData.sourceAngle ? detectionResultsData.sourceAngle[index] : undefined,
					index,
				});
			}
		});
	}

	const portLine = portTrackPoints.map((p) => [p.lat, p.lng]);

	const cpaTrackPoint = cpaIndex !== -1 && detectionTrackPoints[cpaIndex]
		? detectionTrackPoints[cpaIndex]
		: (detectionTrackPoints.length > 0 ? detectionTrackPoints[detectionTrackPoints.length - 1] : null);

	const center = cpaTrackPoint
		? [cpaTrackPoint.lat, cpaTrackPoint.lon]
		: [];

		
	const detectionLabel = record && record.channelName
		? record.channelName
		: (langdata && langdata.RealtimeDetection ? langdata.RealtimeDetection : 'Detection');

	const detectionAlgorithmTypeId = record
		? record.detectionalgorithmtypeId
		: null;

	// Single color for this detection's algorithm — same hash-based lookup
	// used by SeaMap.jsx, so algorithm A is always the same color, B is
	// always a different (but also consistent) color, etc. SeaMap itself
	// still re-derives the color from `detectionAlgorithmTypeId` directly
	// and takes priority over this value; this is only the fallback used
	// if that ID is ever missing.
	const detectionColor = colorForAlgorithmId(detectionAlgorithmTypeId) || ALGORITHM_COLORS[0];

	const detectionTrajectoryPoints = detectionTrackPoints.map((point) => ({
		lat: point.lat,
		lng: point.lon,
		sourceDistance: point.sourceDistance,
		sourceAngle: point.sourceAngle,
		sbr: point.sbr,
		timeStart: point.timeStart,
		timeEnd: point.timeEnd,
		isCPA: point.isCPA,
	}));

	const detectionTrajectories = (record && detectionTrajectoryPoints.length > 0)
		? [
			{
				id: record.id,
				detectionAlgorithmTypeId,
				algorithmName: record.detectionAlgorithmTypeName,
				channelName: record.channelName,
				eventType: record.eventType,
				pidsName: record.pidsName,
				color: detectionColor,
				cpaSourceDistance: detectionResultsData ? detectionResultsData.sourceDistances : null,
				cpaSourceDatetime: detectionResultsData ? detectionResultsData.cpaSourceDatetime : null,
				sourceSpeedms: detectionResultsData ? detectionResultsData.sourceSpeedms : null,
				sourceSpeedKts: detectionResultsData ? detectionResultsData.sourceSpeedKts : null,
				peakCoordinates: record.peakCoordinates ? record.peakCoordinates : {},
				peakDistance: record.peakDistance ? record.peakDistance : '',
				points: detectionTrajectoryPoints,
				// Port points — plotted by SeaMap exactly like `points` (same
				// color / hover) but with a DOTTED line. [] = plot nothing.
				portPoints: portTrackPoints,
				// Per-point start time — same array (indexed 1:1 with
				// `points`) shown in the map hover tooltip via SeaMap's
				// DetectionHoverContent.
				startTimeHover : detectionResultsData.timeStart ? detectionResultsData.timeStart : null 
			},
		]
		: [];

	const detectionStartDateTime = record && record.starttime
		? record.starttime
		: null;

	const tempMapArray = {};
	const teampOptionalMapArray = {};

	mapdata && mapdata.map((item) => {
		const fieldValue = item['regioninfoId'];
		if (!tempMapArray[fieldValue]) {
			tempMapArray[fieldValue] = [];
		}
		tempMapArray[fieldValue].push(item);
	});

	optionalpipedata && optionalpipedata.map((item) => {
		const fieldValue = item['regioninfoId'];
		if (!teampOptionalMapArray[fieldValue]) {
			teampOptionalMapArray[fieldValue] = [];
		}
		teampOptionalMapArray[fieldValue].push(item);
	});

	const pipelineTracks = Object.values(tempMapArray).map((item) =>
		item.map((data) => [data.latitude, data.longitude])
	);

	const fiberTracks = Object.values(tempMapArray).map((item) =>
		item.map((data) => [data.fibreLatitude, data.fibreLongitude])
	);

	const pipelineBorderTracks = Object.values(tempMapArray).map((item) =>
		item.map((data) => [data.latitude, data.longitude])
	);

	const optionalPathTracks = Object.values(teampOptionalMapArray).map((item) =>
		item.map((data) => [data.walkingLatitude, data.walkingLongitude])
	);

	const detectionPointsTableData = detectionTrackPoints.map((point, index) => ({
		key: index,
		point: index + 1,
		latitude: point.lat,
		longitude: point.lon,
		sbr: point.sbr,
		sourceAngle: point.sourceAngle !== null && point.sourceAngle !== undefined ? `${point.sourceAngle}°` : '-',
		maxBeamEnergy: point.maxBeamEnergy,
		sourceDistance: point.sourceDistance !== null && point.sourceDistance !== undefined ? `${Number(point.sourceDistance[index]).toFixed(2)} m` : '-',
		timeStart: point.timeStart ? datetimeCovertertolocal(point.timeStart) : null,
		timeEnd: point.timeEnd ? datetimeCovertertolocal(point.timeEnd) : null,
	}));

	const columns = [
		{
			title: `${langdata && langdata.eventType ? langdata.eventType : 'Event Type'}`,
			key: 'eventType',
			render: () => {
				return newRecord[0]?.eventType || 'No Data';
			},
		},

		// {
		// 	title: `${langdata && langdata.MaxBeamEnergy ? langdata.MaxBeamEnergy : 'Max Beam Energy'}`,
		// 	dataIndex: 'maxBeamEnergy',
		// 	key: 'maxBeamEnergy',
		// },

		{
			title: `${langdata && langdata.SourceDistance ? langdata.SourceDistance + ' (m)' : 'Source Distance (m)'}`,
			dataIndex: 'sourceDistance',
			key: 'sourceDistance',		
		},
		{
			title: `${langdata && langdata.peakDistance ? langdata.peakDistance + ' (m)' : 'Peak Distance (m)'}`,
			key: 'peakDistance',
			render: () => {
				return newRecord[0]?.peakDistance || 'No Data';
			},
		},

		{
			title: `${langdata && langdata.StartTime ? langdata.StartTime : 'Start Time'}`,
			dataIndex: 'timeStart',
			key: 'timeStart',
			render: (value) => (
				value ? `${value.localdate} ${value.localtime}` : '-'
			),
		},

		{
			title: `${langdata && langdata.EndTime ? langdata.EndTime : 'End Time'}`,
			dataIndex: 'timeEnd',
			key: 'timeEnd',
			render: (value) => (
				value ? `${value.localdate} ${value.localtime}` : '-'
			),
		},
	];

	// ---- Custom PLAY logic (restored) ----
	const runPlayLogic = () => {
		setchangePlayrealtimedetectionLine(true);
	};

	const stopPlayLogic = () => {
		setchangePlayrealtimedetectionLine(false);
	};

	useEffect(() => {
		if (!changePlayrealtimedetectionLine) {
			// Play switched off -> clear the port trail too.
			setPlayPortRealtimedetectionLine([]);
			return undefined;
		}

		const maxLength = Math.max(line.length, portLine.length);
		if (!maxLength) return undefined;

		setRealtimedetectionLine([]);
		setPlayPortRealtimedetectionLine([]);
		let index = 0;

		// Source and port points are revealed in the same tick (same index),
		// so both trails animate together, each in its own original order.
		const interval = setInterval(() => {
			if (index >= maxLength) {
				clearInterval(interval);
				return;
			}

			const point = line[index];
			if (point) {
				setRealtimedetectionLine(prev => [...prev, point]);
			}

			const portPoint = portLine[index];
			if (portPoint) {
				setPlayPortRealtimedetectionLine(prev => [...prev, portPoint]);
			}
			// NOTE: the map is intentionally left exactly where the user has
			// it — Play no longer recenters/follows the animated point (it
			// used to call setCenter() here on every tick), so zooming in
			// and starting Play doesn't move or zoom the map away from view.

			index++;
		}, 500);
		return () => clearInterval(interval);

	}, [changePlayrealtimedetectionLine]);

	function ChangeMapCenter({ center }) {
		const map = useMap();

		useEffect(() => {
			if (!center || center.length === 0) return;
			map.setView(center);
		}, [center, map]);

		return null;
	}

	ChangeMapCenter.propTypes = {
		center: PropTypes.arrayOf(PropTypes.number),
	};

	// ---- Custom PLAY overlay — injected into SeaMap's own LayersControl via
	// `layersControlChildren`. This replaces SeaMap's built-in trajectory
	// PLAY control (disabled below via showTrajectoryPlayControl={false}),
	// which wasn't animating correctly on this page. ----
	const pipelineLayersControlChildren = (
		<LayersControl.Overlay name={langdata && langdata.Play ? langdata.Play : 'Play'}>
			<Polyline
				pathOptions={linecolor}
				weight={7}
				positions={changePlayrealtimedetectionLine ? playrealtimedetectionLine : []}
				eventHandlers={{
					add: () => { runPlayLogic(); },
					remove: () => { stopPlayLogic(); }
				}}
			/>
		</LayersControl.Overlay>
	);

	// ---- Extra always-visible elements injected directly into SeaMap's MapContainer ----
	const buildMapChildren = (fullscreenIcon) => (
		<>
			<ChangeMapCenter
				center={center}
			/>
			<button onClick={() => { setSize(size === 'auto' ? '200px' : 'auto'); }} className="zoomButton">{fullscreenIcon}</button>

			{changePlayrealtimedetectionLine && playrealtimedetectionLine && playrealtimedetectionLine.length > 0 && (
				<Marker
					position={{ lat: playrealtimedetectionLine.at(-1)[0], lng: playrealtimedetectionLine.at(-1)[1] }}
					icon={vesselCpaIcon}
				>
					<Popup>{detectionLabel}</Popup>
				</Marker>
			)}

			{/* PORT PLAY trail — dotted green line + moving marker, revealed in
			    parallel with the source trail above. Renders nothing when the
			    detection has no portLatitude/portLongitude. */}
			{changePlayrealtimedetectionLine && playPortRealtimedetectionLine && playPortRealtimedetectionLine.length > 0 && (
				<>
					<Polyline
						pathOptions={portLinecolor}
						weight={7}
						positions={playPortRealtimedetectionLine}
					/>
					<Marker
						position={{ lat: playPortRealtimedetectionLine.at(-1)[0], lng: playPortRealtimedetectionLine.at(-1)[1] }}
						icon={vesselCpaIcon}
					>
						<Popup>{detectionLabel}</Popup>
					</Marker>
				</>
			)}
		</>
	);

	// Effective center used by whichever map is rendered (Leaflet or Google).
	// Play no longer follows/recenters the map, so this always stays the
	// original, correctly-computed center — zooming in and starting Play
	// won't move or zoom the map away from where the user left it.
	const effectiveCenter = center;

	// While PLAY is on, hide this record's static CPA (vesselCpaIcon) marker —
	// it reappears at its original position the instant PLAY is switched off.
	const hiddenTrajectoryCpaIds = changePlayrealtimedetectionLine && record ? [record.id] : [];

	return (
		<>
			{state ?
				<>
					<Row gutter={{
						xs: 8,
						sm: 16,
						md: 24,
						lg: 32,
					}}
					style={{
						margin: '20px',
						paddingLeft: '10px',
						paddingRight: '10px',
						background: '#FFFFFF',
						alignItems: 'center',
						marginTop: '10px',
						marginBottom: '0px'

					}}
					>
						<Col style={{ marginTop: '10px' }} span={12}>
							<Row>
								<Space>
									<Title level={5} style={{ lineHeight: '2', fontWeight: '1' }}> {langdata && langdata.Channel ? langdata.Channel : 'Channel'}: <b>{record ? record.channelName : ''}</b> </Title>
								</Space>
								<Space>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Space>
								<Space>
									<Title level={5} style={{ lineHeight: '2', fontWeight: '1' }}> {langdata && langdata.Date ? langdata.Date : 'Date'}: <b> {detectionStartDateTime ? detectionStartDateTime : '-'}</b> </Title>
								</Space>

							</Row>
						</Col>
					</Row>
					{size && size === '200px' ?
						<Row
							style={{
								margin: '10px',
								paddingLeft: '10px',
								paddingRight: '10px',
								alignItems: 'center',
								marginTop: '10px',
								marginBottom: '0px'
							}}
						>
							<Col className="gutter-row" span={24}>


								<Card>
									<Breadcrumb
										items={[
											{
												href: '/dashboard',
												title: <HomeOutlined />,
											},
											{
												title: (state.path === 'realtimedetections' ? <a onClick={() => { navigate(-1); }}>{langdata && langdata.RealtimeDetection ? langdata.RealtimeDetection : 'Realtime Detection'}</a> : <a onClick={() => { navigate(-1); }}>{langdata && langdata.LineWalkerRealtimeDetections ? langdata.LineWalkerRealtimeDetections : 'Line Walker Realtime Detections'}</a>)
											},
											{
												title: `${langdata && langdata.RealtimeDetectionView ? langdata.RealtimeDetectionView : 'Realtime Detection View'}`,
											},
										]}
										style={{ marginBottom: '10px' }}
									/>

									<Content>

										<div className="App" style={{ textAlign: 'left' }}>

											{map_type && map_type !== 'leaflet' ?  <GoogleMap datadetection={newRecord} height={'30vh'} center={effectiveCenter} showAISVessels={true} coordinatesData={coordinateMarkers} />

												:
												<SeaMap
													center={effectiveCenter}
													showAISVessels={true}
													zoom={17}
													style={{ height: size }}
													mapContainerProps={{ scrollWheelZoom: true }}
													layersControlChildren={pipelineLayersControlChildren}
													mapChildren={buildMapChildren(<FullscreenOutlined />)}
													detectionLabel={detectionLabel}
													detectionTrajectories={detectionTrajectories}
													asnData={state?.asnData || []}
													pipelineTracks={pipelineTracks}
													fiberTracks={fiberTracks}
													pipelineBorderTracks={pipelineBorderTracks}
													optionalPathTracks={optionalPathTracks}
													showFullscreenControl={false}
													showTrajectoryPlayControl={false}
													hiddenTrajectoryCpaIds={hiddenTrajectoryCpaIds}
													kmpMarkers={KMP}
													chMarkers={CH}
													coordinatesData={coordinateMarkers}
													distanceRangeTracks={distanceRangeTracks}
												/>}
										</div>

										<Row style={{
											margin: '10px',
											paddingLeft: '10px',
											paddingRight: '10px',
											background: '#FFFFFF',
											alignItems: 'center',
										}} span={24}
										>
											<Col span={24}>
												<Table
													scroll={{
														x: 900,
													}}
													columns={columns} dataSource={detectionPointsTableData} />

											</Col>
										</Row>

									</Content>

								</Card>
							</Col>
						</Row>

						:
						<div className="App" style={{ textAlign: 'left', margin: '10px', height: '90vh' }}>
							{map_type && map_type !== 'leaflet' ? <GoogleMap datadetection={newRecord} height={'90vh'} center={effectiveCenter} showAISVessels={true} coordinatesData={coordinateMarkers} /> :
								<SeaMap
									center={effectiveCenter}
									zoom={17}
									style={{ height: '100%' }}
									mapContainerProps={{ scrollWheelZoom: true }}
									layersControlChildren={pipelineLayersControlChildren}
									mapChildren={buildMapChildren(<FullscreenExitOutlined />)}
									detectionLabel={detectionLabel}
									detectionTrajectories={detectionTrajectories}
									asnData={state?.asnData || []}
									pipelineTracks={pipelineTracks}
									fiberTracks={fiberTracks}
									pipelineBorderTracks={pipelineBorderTracks}
									optionalPathTracks={optionalPathTracks}
									showFullscreenControl={false}
									showTrajectoryPlayControl={false}
									hiddenTrajectoryCpaIds={hiddenTrajectoryCpaIds}
									kmpMarkers={KMP}
									chMarkers={CH}
									coordinatesData={coordinateMarkers}
									distanceRangeTracks={distanceRangeTracks}
								/>}
						</div>


					}

				</>
				: ''}
		</>
	);
}